import React, { useEffect, useState } from "react";
import TableCompo from "../Table/TableCompo";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  CardContent,
  DialogContent,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { CiEdit } from "react-icons/ci";
import {
  fetchRedeemOrdersRequest,
  createRedeemRequest,
  clearRedeemState,
} from "../../../redux/Order/RedeemOrdersSlice";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import useCsvConverter from "../../../hooks/convertToCSV";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import { canShowPermission } from "../Permissions/permissionObject";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Stack, useMediaQuery, useTheme } from "@mui/material";

const Redeem = () => {
  const dispatch = useDispatch();
  const { convertToCsv } = useCsvConverter();
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [appliedDateRange, setAppliedDateRange] = useState(null);

  const redeemOrders = useSelector((state) => state.redeemOrders || {});
  const { data = [], total = 0 } = redeemOrders;

  const [limit, setLimit] = useState(5);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const payload = { start: 0, limit, search};
    if (startDate && endDate) {
      payload.startDate = formatDateTimeRange(startDate, "start");
      payload.endDate = formatDateTimeRange(endDate, "end");
    }
    dispatch(fetchRedeemOrdersRequest(payload));
  }, [dispatch, page, limit, search,]);

  const handleGetDetails = () => {
  if (!startDate || !endDate) {
    toast.error("Please select both start and end dates.");
    return;
  }
  if (startDate > endDate) {
    toast.error("Start Date cannot be after End Date");
    return;
  }

  setPage(0);
  dispatch(
    fetchRedeemOrdersRequest({
      start: 0,
      limit,
      search,
      startDate: formatDateTimeRange(startDate, "start"),
      endDate: formatDateTimeRange(endDate, "end"),
    })
  );
};

  const { error, lastAction } = useSelector(
    (state) => state.redeemOrders || {}
  );

  const package_name = useSelector(
    (state) => state?.appConfig?.configData?.package_name || "",
  );

  const decimal = useSelector(
      (state) => state?.appConfig?.configData?.decimalPrecision || 4,
    );

  useEffect(() => {
    if (error && lastAction === "createRedeem") {
      toast.error(error);
      setLoading(false);
      dispatch(clearRedeemState());
    }
  }, [error, lastAction]);

  const handleEditClick = (row) => {
    setSelectedRow(row);
    setInputValue(""); // reset OTP
    setOpen(true);
  };

  const handleApprove = () => {
    const trimmedOtp = (inputValue || "").trim();

    if (!selectedRow || !trimmedOtp) {
      toast.error("Please enter a valid OTP before approving.");
      return;
    }

    const data = selectedRow;
    const id = data._id;
    const customerId = data.customer._id;

    let redeemtype = "Holding";
    if (data.metalName === "Money") {
      redeemtype = "Money";
    } else if (data.sip) {
      redeemtype = "Sip";
    } else if (data.gifts) {
      redeemtype = "Gift";
    }

    setLoading(true);

    // simulate API delay
    setTimeout(() => {
      dispatch(
        createRedeemRequest({
          redeemid: id,
          redeemtype,
          customer: customerId,
          otp: trimmedOtp,
        })
      );
      setLoading(false);
      handleClose();
    }, 300);
  };

  const formatDateTimeRange = (date, type) => {
  if (!date) return null;
  const d = new Date(date);
  if (type === "start") {
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

const handleExportRedeem = () => {
  if (!startDate || !endDate) {
    toast.error("Please select both start and end dates.");
    return;
  }
  if (startDate > endDate) {
    toast.error("Start Date cannot be after End Date");
    return;
  }

  setExporting(true);
  dispatch(
    apiRequest({
      method: "POST",
      endpoint: "/api/v1/redeem/exportRedeemOrders",
      body: {
        startDate: formatDateTimeRange(startDate, "start"),
        endDate: formatDateTimeRange(endDate, "end"),
      },
      callback: (response) => {
        setExporting(false);
        if (response?.success) {
          toast.success(response.message || "Export started.");
        } else {
          toast.error(response?.message || "Export failed.");
        }
      },
    }),
  );
};

  const handleClose = () => {
    if (loading) return; // prevent closing while approving
    setOpen(false);
    setSelectedRow(null);
    setInputValue("");
  };

  const handleTableChange = (action, tableState) => {
    const dateParams =
    startDate && endDate
      ? {
          startDate: formatDateTimeRange(startDate, "start"),
          endDate: formatDateTimeRange(endDate, "end"),
        }
      : {};
    switch (action) {
      case "changePage":
        setPage(tableState.page);
        dispatch(
          fetchRedeemOrdersRequest({
            start: tableState.page * tableState.rowsPerPage,
            limit: tableState.rowsPerPage,
            search,
            ...dateParams,
          })
        );
        break;

      case "changeRowsPerPage":
        setLimit(tableState.rowsPerPage);
        setPage(0);
        dispatch(
          fetchRedeemOrdersRequest({
            start: 0,
            limit: tableState.rowsPerPage,
            search,
            ...dateParams,
          })
        );
        break;

      case "search":
        const searched = (tableState.searchText || "").trim();
        setSearch(searched);
        setPage(0);
        dispatch(
          fetchRedeemOrdersRequest({
            start: 0,
            limit,
            search: searched,
            ...dateParams,
          })
        );
        break;

      default:
        break;
    }
  };

  const [permissions, setPermissions] = useState(null);
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error("Invalid permissions JSON", e);
        setPermissions({});
      }
    } else {
      setPermissions({});
    }
  }, []);

  if (permissions === null) {
    return null;
  }

  const canEdit = canShowPermission(permissions, "redeem", "edit");

  const columns = [
    ...(canEdit
      ? [
          {
            field: "_id-edit",
            headerName: "Edit",
            minWidth: 100,
            render: (value, row) =>
              row.status === "PENDING" ? (
                <CiEdit
                  style={{ cursor: "pointer", fontSize: "24px" }}
                  onClick={() => handleEditClick(row)}
                />
              ) : null,
          },
        ]
      : []),
    {
      field: "createdAt",
      headerName: "Created At",
      render: (val, row) => {
        return row?.createdAt
          ? new Date(row.createdAt).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
          : "N.A";
      },
    },
    {
      field: "customer",
      headerName: "Customer Name",
      render: (val, row) =>
        row.customer
          ? `${row.customer.firstName || ""} ${row.customer.middleName || ""} ${row.customer.lastName || ""}`
          : "N.A",
    },
    {
      field: "phone",
      headerName: "Phone",
      render: (val, row) =>
        row.customer ? `${row.customer.phone || ""}` : "N.A",
    },
    {
      field: "bullion",
      headerName: "Metal",
      render: (val, row) => {
        const bullion = row?.bullion;
        if (!bullion) return "N.A";

        const metalName = bullion.metalName || "";
        const karat = bullion.karat ? `${bullion.karat}K` : "";
        const purity = bullion.purity?.toString() || "";

        const combined = [metalName, karat, purity].filter(Boolean).join(" ");

        return combined || row.metalName;
      },
    },
    {
      field: "weight",
      headerName: "Weight",
      render: (val, row) => {
        const weight = Number(row?.weight || 0).toFixed(decimal);
        return weight;
      }
    },
    {
      field: "amount",
      headerName: "Amount",
      render: (val, row) => {
        const amount = Number(row?.amount || 0).toFixed(2);
        return amount;
      }
    },
    {
      field: "status",
      headerName: "Status",
      render: (val, row) => {
        let color;
        switch (row.status) {
          case "SUCCESS":
            color = "green";
            break;
          case "PENDING":
            color = "blue";
            break;
          default:
            color = "red";
            break;
        }

        return <span style={{ color, fontWeight: "bold" }}>{row.status}</span>;
      },
    },
  ];

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadStoreCsv = () => {
    console.log("Store CSV API called");

    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/redeem/redeemCsv",
        query: {},
        callback: (response) => {
          console.log("Store CSV response:", response);

          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, "redeem.csv");
        },
      })
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100vh", overflow: "auto" }}>
      <Box
        sx={{
          width: "100%",
          mt: 0,
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography fontWeight={700} fontSize={24} p={2} fontFamily={"Aeonik"}>
          Redeem List
        </Typography>

        <CardContent sx={{ height: "580px", width: "100%" }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
              display="flex"
              flexDirection={isMobile ? "column" : "row"}
              gap={1.5}
              px={2}
              py={1}
            >
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                maxDate={endDate || new Date()}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      minWidth: 160,
                      "& .MuiInputBase-root": { height: 36, fontSize: 13 },
                      "& .MuiInputLabel-root": { fontSize: 13 },
                    },
                  },
                }}
              />

              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                minDate={startDate}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      minWidth: 160,
                      "& .MuiInputBase-root": { height: 36, fontSize: 13 },
                      "& .MuiInputLabel-root": { fontSize: 13 },
                    },
                  },
                }}
              />

              <Button
                variant="outlined"
                fullWidth={isMobile}
                sx={{
                  height: 36,
                  minWidth: 120,
                  fontSize: 13,
                  textTransform: "none",
                  px: 2,
                  whiteSpace: "nowrap",
                }}
                  onClick={handleGetDetails}
              >
                  Get Details
              </Button>
            </Box>
          </LocalizationProvider>
          <TableCompo
            columns={columns}
            data={[...JSON.parse(JSON.stringify(data))]}
            totalCount={total}
            page={page}
            limit={limit}
            onTableChange={handleTableChange}
            extraButtons={
              <Stack direction="row" spacing={2}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleExportRedeem}
                  disabled={exporting}
                >
                  {exporting ? "Exporting..." : "Export Report"}
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  component="a"
                  href={`${
                    process.env.REACT_APP_API_BASE_URL
                    }/static_data/${package_name}/redeem.xlsx?nocache=${Date.now()}`}
                  download="redeem.xlsx"
                >
                  Download
                </Button>
              </Stack>
            }
          />
        </CardContent>
      </Box>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Redeem Order Details
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={loading}
            sx={{
              color: (theme) => theme.palette.grey[500],
              padding: 0,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <p>Please Enter OTP</p>
          <TextField
            fullWidth
            margin="normal"
            label="Enter OTP"
            value={inputValue}
            onChange={(e) => {
              const value = e.target.value.trim();
              if (/^\d{0,4}$/.test(value)) {
                setInputValue(value);
              }
            }}
            inputProps={{ maxLength: 4 }}
            disabled={loading}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleApprove}
            disabled={loading}
            sx={{ mt: 2, minWidth: 120 }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Approve"
            )}
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Redeem;
